/**
 * PullToRefreshHook - Pull down to refresh content
 *
 * Features:
 * - Pull down gesture at top of scrollable container
 * - Visual spinner indicator during pull and refresh
 * - Configurable threshold and resistance
 * - Triggers LiveView event for data refresh
 * - Haptic feedback on supported devices
 *
 * Usage:
 * <div phx-hook="PullToRefreshHook"
 *      data-refresh-event="refresh_leads"
 *      data-threshold="80"
 *      id="scrollable-container">
 *   <!-- Scrollable content -->
 * </div>
 */
const PullToRefreshHook = {
  mounted() {
    // Configuration
    this.threshold = parseInt(this.el.dataset.threshold || '80', 10);
    this.refreshEvent = this.el.dataset.refreshEvent || 'refresh';
    this.resistance = 2.5; // How much to resist pull (higher = more resistance)

    // State
    this.isPulling = false;
    this.isRefreshing = false;
    this.pullDistance = 0;
    this.startY = 0;
    this.startScrollTop = 0;

    // Create indicator element
    this.createIndicator();

    // Bind event handlers
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);

    // Attach listeners to the element
    this.el.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.el.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.el.addEventListener('touchend', this.handleTouchEnd, { passive: true });

    // Listen for refresh complete from LiveView
    this.handleEvent('refresh_complete', () => {
      this.completeRefresh();
    });
  },

  // Called before LiveView updates the DOM - indicator may be removed by morphdom
  beforeUpdate() {
    try {
      // Store indicator state before update
      this.indicatorWasVisible = this.indicator && this.indicator.style && this.indicator.style.opacity !== '0';
    } catch (e) {
      console.warn('PullToRefreshHook beforeUpdate error:', e);
    }
  },

  // Called after LiveView updates the DOM - re-attach indicator if it was removed
  updated() {
    try {
      this.ensureIndicator();
    } catch (e) {
      console.warn('PullToRefreshHook updated error:', e);
    }
  },

  destroyed() {
    this.el.removeEventListener('touchstart', this.handleTouchStart);
    this.el.removeEventListener('touchmove', this.handleTouchMove);
    this.el.removeEventListener('touchend', this.handleTouchEnd);
    this.removeIndicator();
  },

  // Ensure indicator exists in DOM (may have been removed by morphdom)
  ensureIndicator() {
    if (!this.el || !this.el.id) return;

    const existingIndicator = document.getElementById(`pull-indicator-${this.el.id}`);
    if (!existingIndicator) {
      // Indicator was removed by morphdom, recreate it
      this.createIndicator();

      // Restore visibility state if it was visible during refresh
      if (this.isRefreshing && this.indicator && this.spinner && this.text) {
        this.indicator.style.transform = 'translateX(-50%) translateY(20px)';
        this.indicator.style.opacity = '1';
        this.spinner.style.animation = 'spin 0.8s linear infinite';
        this.text.textContent = 'Refreshing...';
        this.text.style.color = '#0c99eb';
      }
    } else if (existingIndicator !== this.indicator) {
      // Update reference if it changed
      this.indicator = existingIndicator;
      this.spinner = this.indicator.querySelector('div');
      this.text = this.indicator.querySelector('span');
    }
  },

  createIndicator() {
    // Create container for the pull indicator
    this.indicator = document.createElement('div');
    this.indicator.id = `pull-indicator-${this.el.id}`;
    this.indicator.setAttribute('aria-live', 'polite');
    this.indicator.setAttribute('role', 'status');
    Object.assign(this.indicator.style, {
      position: 'absolute',
      left: '50%',
      top: '0',
      transform: 'translateX(-50%) translateY(-100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '12px',
      zIndex: '50',
      transition: 'opacity 200ms ease-out',
      opacity: '0',
      pointerEvents: 'none'
    });

    // Create spinner
    this.spinner = document.createElement('div');
    Object.assign(this.spinner.style, {
      width: '24px',
      height: '24px',
      border: '3px solid rgba(12, 153, 235, 0.3)',
      borderTopColor: '#0c99eb',
      borderRadius: '50%',
      transition: 'transform 200ms ease-out'
    });

    // Create text
    this.text = document.createElement('span');
    Object.assign(this.text.style, {
      fontSize: '12px',
      color: '#888',
      whiteSpace: 'nowrap'
    });
    this.text.textContent = 'Pull to refresh';

    this.indicator.appendChild(this.spinner);
    this.indicator.appendChild(this.text);

    // Make container relative for absolute positioning
    if (getComputedStyle(this.el).position === 'static') {
      this.el.style.position = 'relative';
    }

    this.el.insertBefore(this.indicator, this.el.firstChild);
  },

  removeIndicator() {
    if (this.indicator && this.indicator.parentNode) {
      this.indicator.parentNode.removeChild(this.indicator);
    }
  },

  getScrollTop() {
    // Check if element has its own scroll context
    const elScrollTop = this.el.scrollTop || 0;
    if (elScrollTop > 0 || this.el.scrollHeight > this.el.clientHeight) {
      return elScrollTop;
    }
    // Fall back to window/document scroll
    return window.scrollY || document.documentElement.scrollTop || 0;
  },

  handleTouchStart(e) {
    // Don't start if already refreshing
    if (this.isRefreshing) return;

    // Only start pull if at top of scroll (either element or page)
    const scrollTop = this.getScrollTop();
    if (scrollTop > 5) return;

    const touch = e.touches[0];
    this.startY = touch.clientY;
    this.startScrollTop = scrollTop;
    this.isPulling = true;
    this.pullDistance = 0;
  },

  handleTouchMove(e) {
    if (!this.isPulling || this.isRefreshing) return;

    const touch = e.touches[0];
    const deltaY = touch.clientY - this.startY;

    // Only pull if scrolling down from top
    if (deltaY < 0 || this.getScrollTop() > 0) {
      this.isPulling = false;
      this.resetIndicator();
      return;
    }

    // Apply resistance to pull distance
    this.pullDistance = deltaY / this.resistance;

    // Prevent default scroll behavior when pulling
    if (this.pullDistance > 10) {
      e.preventDefault();
    }

    // Update indicator position and state
    this.updateIndicator();
  },

  handleTouchEnd() {
    if (!this.isPulling || this.isRefreshing) {
      this.isPulling = false;
      return;
    }

    this.isPulling = false;

    // Check if pull exceeded threshold
    if (this.pullDistance >= this.threshold) {
      this.startRefresh();
    } else {
      this.resetIndicator();
    }
  },

  updateIndicator() {
    const progress = Math.min(this.pullDistance / this.threshold, 1);

    // Position indicator
    const translateY = Math.min(this.pullDistance, this.threshold + 20);
    this.indicator.style.transform = `translateX(-50%) translateY(${translateY - 60}px)`;
    this.indicator.style.opacity = Math.min(progress * 1.5, 1).toString();

    // Rotate spinner based on progress
    const rotation = progress * 360;
    this.spinner.style.transform = `rotate(${rotation}deg)`;

    // Update text based on progress
    if (progress >= 1) {
      this.text.textContent = 'Release to refresh';
      this.text.style.color = '#0c99eb';
    } else {
      this.text.textContent = 'Pull to refresh';
      this.text.style.color = '#888';
    }
  },

  resetIndicator() {
    this.pullDistance = 0;
    this.indicator.style.transform = 'translateX(-50%) translateY(-100%)';
    this.indicator.style.opacity = '0';
    this.spinner.style.transform = 'rotate(0deg)';
    this.spinner.style.animation = 'none';
    this.text.textContent = 'Pull to refresh';
    this.text.style.color = '#888';
  },

  startRefresh() {
    this.isRefreshing = true;

    // Position indicator at fixed spot
    this.indicator.style.transform = `translateX(-50%) translateY(20px)`;
    this.indicator.style.opacity = '1';

    // Start spinner animation
    this.spinner.style.animation = 'spin 0.8s linear infinite';

    // Update text
    this.text.textContent = 'Refreshing...';
    this.text.style.color = '#0c99eb';

    // Trigger haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }

    // Push event to LiveView
    this.pushEvent(this.refreshEvent, {});

    // Set timeout to auto-complete if LiveView doesn't respond
    this.refreshTimeout = setTimeout(() => {
      this.completeRefresh();
    }, 5000);
  },

  completeRefresh() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }

    // Show success briefly
    this.text.textContent = 'Updated!';

    // Reset after short delay
    setTimeout(() => {
      this.isRefreshing = false;
      this.resetIndicator();
    }, 500);
  }
};

export default PullToRefreshHook;
